import { RefObject, useLayoutEffect, useRef, useState } from 'react';

const getMinTableZoom = (columnCount: number) => {
  if (columnCount >= 18) return 0.72;
  if (columnCount >= 17) return 0.75;
  return 0.8;
};

const SAFETY_GAP = 8;

const useTableAutoZoom = (
  containerRef: RefObject<HTMLElement | null>,
  zoomTargetRef: RefObject<HTMLElement | null>, // the element with style={{ zoom }}
  columns: unknown[],
  enabled: boolean
) => {
  const [tableZoom, setTableZoom] = useState(1);
  const frameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const zoomTarget = zoomTargetRef.current;

    if (!container || !zoomTarget) return;

    if (!enabled) {
      setTableZoom(1);
      return;
    }

    const calculateZoom = () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);

      frameRef.current = requestAnimationFrame(() => {
        const previousZoom = zoomTarget.style.zoom;

        zoomTarget.style.zoom = '1';

        const headerCells = Array.from(container.querySelectorAll<HTMLTableCellElement>('thead th'));

        if (!headerCells.length) {
          zoomTarget.style.zoom = previousZoom;
          return;
        }

        const requiredWidth = headerCells.reduce((total, cell) => total + cell.getBoundingClientRect().width, 0);
        const availableWidth = container.clientWidth - SAFETY_GAP;

        const minZoom = getMinTableZoom(columns.length);
        const nextZoom = requiredWidth ? Math.max(minZoom, Math.min(1, availableWidth / requiredWidth)) : 1;

        zoomTarget.style.zoom = String(nextZoom);
        setTableZoom(nextZoom);
      });
    };

    calculateZoom();

    const resizeObserver = new ResizeObserver(calculateZoom);
    resizeObserver.observe(container);
    window.addEventListener('resize', calculateZoom);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateZoom);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [containerRef, zoomTargetRef, columns.length, enabled]);

  return tableZoom;
};

export default useTableAutoZoom;
